import { BarChart3 } from 'lucide-react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { defaultChartOptions, energyColors } from './charts/chartConfig';
import type { DailyEnergyMixPoint } from '@/types/energy';
import { formatDayLabel } from '@/lib/todayLine';

interface EnergyMixChartProps {
  data: DailyEnergyMixPoint[];
}

export function EnergyMixChart({ data }: EnergyMixChartProps) {
  if (data.length === 0) {
    return (
      <Card className="relative">
        <div className="absolute top-1.5 right-2 z-10">
          <InfoTooltip
            content={
              <div className="space-y-1">
                <p className="font-semibold">Energy Mix by Day</p>
                <p>Stacked area chart showing daily energy consumption broken down by source: solar (green), grid (orange), and generator (gray).</p>
                <p className="text-muted-foreground">Visualizes how energy sources contribute to total consumption each day. Watch how solar production varies with weather and seasonality.</p>
              </div>
            }
          />
        </div>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-1">
            <BarChart3 className="h-3.5 w-3.5 text-[#8FB560]" />
            <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Energy Mix by Day (kWh)
            </h3>
          </div>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <p className="text-muted-foreground">No energy mix data available</p>
        </CardContent>
      </Card>
    );
  }


  const options: ApexOptions = {
    ...defaultChartOptions,
    chart: {
      ...defaultChartOptions.chart,
      type: 'area',
      stacked: true,
      height: 200,
    },
    colors: [energyColors.solar, '#f59e0b', '#6b7280'],
    fill: {
      type: 'solid',
      opacity: [0.85, 0.75, 0.65],
    },
    stroke: {
      width: 0,
      curve: 'smooth',
    },
    xaxis: {
      categories: data.map(d => formatDayLabel(d.date)),
      labels: {
        style: { colors: '#9ca3af', fontSize: '10px' },
        rotate: 0,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickAmount: Math.min(data.length - 1, 6),
    },
    yaxis: {
      labels: {
        formatter: (val) => `${Math.round(val)}`,
        style: { colors: '#9ca3af', fontSize: '10px' },
      },
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${Math.round(val)} kWh`,
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      labels: { colors: '#9ca3af' },
      markers: {
        size: 8,
        strokeWidth: 0,
      },
      itemMargin: {
        horizontal: 12,
      },
    },
  };

  const series = [
    { name: 'Solar', data: data.map(d => d.solarKwh) },
    { name: 'Grid', data: data.map(d => d.gridKwh) },
    { name: 'Generator', data: data.map(d => d.generatorKwh) },
  ];

  return (
    <Card className="relative">
      <div className="absolute top-1.5 right-2 z-10">
        <InfoTooltip
          content={
            <div className="space-y-1">
              <p className="font-semibold">Energy Mix by Day</p>
              <p>Stacked area chart showing daily energy consumption broken down by source: solar (green), grid (orange), and generator (gray).</p>
              <p className="text-muted-foreground">Visualizes how energy sources contribute to total consumption each day. Watch how solar production varies with weather and seasonality.</p>
            </div>
          }
        />
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-1">
          <BarChart3 className="h-3.5 w-3.5 text-[#8FB560]" />
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Energy Mix by Day (kWh)
          </h3>
        </div>
      </CardHeader>
      <CardContent>
        <Chart
          type="area"
          height={220}
          options={options}
          series={series}
        />
      </CardContent>
    </Card>
  );
}
